import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useApp } from "../context/AppContext";
import { authFetch, api, getStoredRefreshToken } from "../utils/api";
import { t } from "../utils/i18n";
import { useNavigate, useLocation } from "react-router-dom";
import LanguagePicker from "../components/LanguagePicker";
import AddressSearch from "../components/AddressSearch";
import {
  User, Lock, ShieldCheck, Calendar, Megaphone, MessageCircle, Eye, EyeOff, Send, MapPin, UserX,
  Users, UserPlus, Star, CreditCard, Receipt, Landmark, Fingerprint, Smartphone, PauseCircle,
  Trash2, Scale, HelpCircle, BookOpen, Info, LogOut, TrendingUp, Search, X, Plus, Globe,
  BadgeCheck, XCircle, Phone, Heart, Briefcase, Euro, Bell, Building2, FileText, Download, type LucideIcon,
} from "lucide-react";
import { useIsMobile } from "../hooks/useIsMobile";

// Set constant réutilisé pour les appels de DoubleJeuFriendGroup sans second groupe à exclure
// (ex. "Amis choisis", qui n'a qu'une seule liste — contrairement au Double jeu, deux).
const EMPTY_ID_SET = new Set<string>();

// Sélecteur d'amis pour un groupe de "Double jeu" — barre de recherche + suggestions limitées à 3
// (plutôt que de dérouler toute la liste d'amis, qui peut compter des milliers d'entrées), et les
// amis déjà choisis toujours visibles en pastilles au-dessus (jamais "perdus" hors de vue), pour
// que l'utilisateur sache toujours exactement qui voit quoi.
function DoubleJeuFriendGroup({
  accent, accentSoft, friends, selectedIds, otherIds, otherLabel, onToggle, search, onSearchChange, lang,
}: {
  accent: string; accentSoft: string;
  friends: { id: string; name: string; avatar_url?: string }[];
  selectedIds: Set<string>; otherIds: Set<string>; otherLabel: string;
  onToggle: (id: string) => void; search: string; onSearchChange: (v: string) => void; lang: string;
}) {
  const selected = friends.filter(f => selectedIds.has(f.id));
  const query = search.trim().toLowerCase();
  const candidates = friends.filter(f => !selectedIds.has(f.id) && (!query || (f.name || "").toLowerCase().includes(query)));
  const visible = query ? candidates.slice(0, 30) : candidates.slice(0, 3);

  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ fontSize: 12, color: "#999", marginBottom: 10 }}>
        {selected.length === 0 ? t(lang, "nobody_yet") : `${t(lang, "visible_by_prefix")} ${selected.length} ${selected.length > 1 ? t(lang, "person_plural") : t(lang, "person_singular")}`}
      </div>

      {selected.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          {selected.map(f => (
            <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px 4px 4px", borderRadius: 999, backgroundColor: accentSoft }}>
              <div style={{ width: 20, height: 20, borderRadius: "50%", overflow: "hidden", backgroundColor: accent, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 9, fontWeight: 700, flexShrink: 0 }}>
                {f.avatar_url ? <img src={f.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (f.name || "?")[0]}
              </div>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: accent }}>{(f.name || "").split(" ")[0]}</span>
              <button onClick={() => onToggle(f.id)} style={{ border: "none", background: "none", cursor: "pointer", padding: 0, display: "flex", color: accent, opacity: 0.7 }}>
                <X size={13} strokeWidth={2.5} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ position: "relative", marginBottom: 6 }}>
        <Search size={15} color="#B0B0B0" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
        <input
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          placeholder={t(lang, "search_friend_placeholder")}
          style={{ width: "100%", padding: "9px 12px 9px 34px", borderRadius: 10, border: "1.5px solid #E5E5E5", fontSize: 13.5, outline: "none", boxSizing: "border-box" }}
        />
      </div>

      {friends.length === 0 ? (
        <div style={{ fontSize: 13, color: "#999", padding: "6px 0" }}>{t(lang, "no_friends_yet")}</div>
      ) : visible.length === 0 ? (
        <div style={{ fontSize: 12.5, color: "#bbb", padding: "6px 0" }}>{query ? t(lang, "no_friend_found") : t(lang, "all_friends_assigned")}</div>
      ) : (
        visible.map(f => {
          const inOther = otherIds.has(f.id);
          return (
            <div key={f.id} onClick={() => onToggle(f.id)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 4px", borderRadius: 10, cursor: "pointer", opacity: inOther ? 0.45 : 1 }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", overflow: "hidden", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 10.5, flexShrink: 0 }}>
                {f.avatar_url ? <img src={f.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (f.name || "?")[0]}
              </div>
              <div style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{f.name}{inOther ? ` · ${otherLabel}` : ""}</div>
              <div style={{ width: 24, height: 24, borderRadius: "50%", border: `1.5px solid ${accent}55`, display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0 }}>
                <Plus size={13} strokeWidth={2.5} />
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

const TX_STATUS_KEYS: Record<string, string> = {
  pending_payment: "tx_status_pending", succeeded: "tx_status_succeeded", disputed: "tx_status_disputed",
  refunded: "tx_status_refunded", released: "tx_status_released", awaiting_payout_setup: "tx_status_awaiting_payout",
};
const txStatusLabel = (lang: string, status: string) => TX_STATUS_KEYS[status] ? t(lang, TX_STATUS_KEYS[status]) : status;

const LANGS = [
  { code:"fr", flag:"🇫🇷", label:"Français" },
  { code:"en", flag:"🇬🇧", label:"English" },
  { code:"es", flag:"🇪🇸", label:"Español" },
  { code:"ar", flag:"🇸🇦", label:"العربية" },
  { code:"de", flag:"🇩🇪", label:"Deutsch" },
  { code:"it", flag:"🇮🇹", label:"Italiano" },
  { code:"pt", flag:"🇵🇹", label:"Português" },
  { code:"zh", flag:"🇨🇳", label:"中文" },
  { code:"ru", flag:"🇷🇺", label:"Русский" },
  { code:"tr", flag:"🇹🇷", label:"Türkçe" },
  { code:"nl", flag:"🇳🇱", label:"Nederlands" },
  { code:"pl", flag:"🇵🇱", label:"Polski" },
];

function Toggle({ value, onChange }: { value: boolean; onChange: () => void }) {
  return (
    <div onClick={onChange} style={{ width: 44, height: 24, borderRadius: 12, backgroundColor: value ? "#0a0a0a" : "#E0E0E0", position: "relative", cursor: "pointer", flexShrink: 0, transition: "background 0.2s" }}>
      <div style={{ position: "absolute", top: 2, left: value ? 22 : 2, width: 20, height: 20, borderRadius: "50%", backgroundColor: "white", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
    </div>
  );
}

function SectionTitle({ title }: { title: string }) {
  return <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: 0.8, color: "#999", padding: "16px 20px 6px" }}>{title}</div>;
}

function ItemIcon({ icon: Icon, danger }: { icon: LucideIcon; danger?: boolean }) {
  return (
    <div style={{ width: 34, height: 34, borderRadius: 10, backgroundColor: danger ? "#FBEAEA" : "#F5F5F5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Icon size={17} strokeWidth={1.75} color={danger ? "#c0392b" : "#0a0a0a"} />
    </div>
  );
}

function SettingItem({ icon, label, sub, onPress, right, danger }: { icon: LucideIcon; label: string; sub?: string; onPress?: () => void; right?: React.ReactNode; danger?: boolean }) {
  return (
    <div onClick={onPress} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 20px", borderBottom: "1px solid #F8F8F8", cursor: onPress ? "pointer" : "default" }}>
      <ItemIcon icon={icon} danger={danger} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: danger ? "#0a0a0a" : "#0a0a0a" }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{sub}</div>}
      </div>
      {right !== undefined ? right : onPress && <span style={{ color: "#ccc", fontSize: 18 }}>›</span>}
    </div>
  );
}

function ToggleItem({ icon, label, sub, value, onToggle }: { icon: LucideIcon; label: string; sub?: string; value: boolean; onToggle: () => void }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 20px", borderBottom: "1px solid #F8F8F8" }}>
      <ItemIcon icon={icon} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: "#0a0a0a" }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{sub}</div>}
      </div>
      <Toggle value={value} onChange={onToggle} />
    </div>
  );
}

// Choix "Tout le monde / Mes amis" en ligne — remplace l'ancien SelectItem à accordéon (clic pour
// dérouler 2 options) : un aller-retour de moins pour un simple bouton à deux états, changement
// immédiat et visible sans repliage/dépliage.
function SegmentedItem({ icon, label, value, onChange, lang }: { icon: LucideIcon; label: string; value: "everyone" | "friends"; onChange: (v: "everyone" | "friends") => void; lang: string }) {
  return (
    <div style={{ padding: "13px 20px", borderBottom: "1px solid #F8F8F8" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
        <ItemIcon icon={icon} />
        <div style={{ fontSize: 14, fontWeight: 500, color: "#0a0a0a" }}>{label}</div>
      </div>
      <div style={{ display: "flex", backgroundColor: "#F5F5F5", borderRadius: 10, padding: 3, gap: 3 }}>
        {(["everyone", "friends"] as const).map(v => {
          const active = value === v;
          return (
            <div
              key={v}
              onClick={() => onChange(v)}
              style={{ flex: 1, textAlign: "center", padding: "8px 0", borderRadius: 8, cursor: "pointer", fontSize: 12.5, fontWeight: active ? 700 : 500, color: active ? "#0a0a0a" : "#999", backgroundColor: active ? "white" : "transparent" }}
            >
              {v === "everyone" ? t(lang, "visibility_everyone") : t(lang, "visibility_friends_only")}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Settings() {
  const { lang, setLang, setToken, setUser, user, token, bumpLocationSharing } = useApp();
  const location = useLocation();
  const sName = user?.name || t(lang, "default_user_name");
  const sInitials = sName.split(" ").map((n: string) => n[0]).join("").slice(0,2).toUpperCase();
  const sHandle = "@" + (user?.name?.toLowerCase().replace(/\s/g,"") || "user");
  const sCity = user?.city || "";
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdMsg, setPwdMsg] = useState("");
  const [pwdLoading, setPwdLoading] = useState(false);

  // Personnes bloquées
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState<any[]>([]);
  const [loadingBlocked, setLoadingBlocked] = useState(false);

  const openBlockedModal = () => {
    setShowBlockedModal(true);
    setLoadingBlocked(true);
    api.get("/users/me/blocked").then(data => setBlockedUsers(data?.blocked || [])).catch(() => {}).finally(() => setLoadingBlocked(false));
  };

  const unblockUser = (id: string) => {
    api.delete(`/users/${id}/block`).then(() => setBlockedUsers(prev => prev.filter(u => u.id !== id))).catch(() => {});
  };

  // Personnes masquées — différent d'un blocage : à sens unique, la personne masquée continue de
  // voir/contacter normalement, seul son contenu disparaît du fil de qui l'a masquée.
  const [showHiddenModal, setShowHiddenModal] = useState(false);
  const [hiddenUsers, setHiddenUsers] = useState<any[]>([]);
  const [loadingHidden, setLoadingHidden] = useState(false);

  const openHiddenModal = () => {
    setShowHiddenModal(true);
    setLoadingHidden(true);
    api.get("/users/me/hidden").then(data => setHiddenUsers(data?.hidden || [])).catch(() => {}).finally(() => setLoadingHidden(false));
  };

  const unhideUser = (id: string) => {
    api.delete(`/users/${id}/hide`).then(() => setHiddenUsers(prev => prev.filter(u => u.id !== id))).catch(() => {});
  };

  // Historique des transactions
  const [showTxModal, setShowTxModal] = useState(false);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loadingTx, setLoadingTx] = useState(false);
  const openTxModal = () => {
    setShowTxModal(true);
    setLoadingTx(true);
    api.get("/payments/me").then(data => setTransactions(data?.payments || [])).catch(() => {}).finally(() => setLoadingTx(false));
  };

  // Mes revenus (vendeur/prestataire) + statut du compte bancaire Stripe Connect
  const [showEarningsModal, setShowEarningsModal] = useState(false);
  const [earningsStats, setEarningsStats] = useState<any>(null);
  const [earningsRows, setEarningsRows] = useState<any[]>([]);
  const [loadingEarnings, setLoadingEarnings] = useState(false);
  const [connectStatus, setConnectStatus] = useState<{ onboarded: boolean; payouts_enabled: boolean } | null>(null);
  const [connectLoading, setConnectLoading] = useState(false);

  useEffect(() => {
    api.get("/connect/status").then(data => setConnectStatus(data)).catch(() => {});
  }, []);

  const openEarningsModal = () => {
    setShowEarningsModal(true);
    setLoadingEarnings(true);
    api.get("/payments/me/earnings").then(data => { setEarningsStats(data?.stats || null); setEarningsRows(data?.payments || []); }).catch(() => {}).finally(() => setLoadingEarnings(false));
  };

  // --- Identité de facturation + factures (modèle d'intermédiaire transparent) --------------
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billing, setBilling] = useState<any>({ legal_name: "", legal_form: "", siren: "", siret: "", vat_applicable: false, vat_number: "", vat_rate: 20, billing_address: "", billing_postal_code: "", billing_city: "" });
  const [savingBilling, setSavingBilling] = useState(false);
  const [billingErr, setBillingErr] = useState("");
  const openBillingModal = () => {
    setShowBillingModal(true);
    setBillingErr("");
    api.get("/billing/profile").then(data => { if (data?.profile) setBilling({ ...data.profile, vat_rate: Number(data.profile.vat_rate) || 20 }); }).catch(() => {});
  };
  const saveBilling = async () => {
    setSavingBilling(true); setBillingErr("");
    try {
      const res = await authFetch("/billing/profile", { method: "PUT", body: JSON.stringify(billing) });
      const data = await res.json();
      if (!res.ok) { setBillingErr(data?.error || t(lang, "error_connection")); return; }
      setShowBillingModal(false);
    } catch { setBillingErr(t(lang, "error_connection")); }
    finally { setSavingBilling(false); }
  };

  const [showInvoicesModal, setShowInvoicesModal] = useState(false);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const openInvoicesModal = () => {
    setShowInvoicesModal(true);
    setLoadingInvoices(true);
    api.get("/billing/invoices").then(data => setInvoices(data?.invoices || [])).catch(() => {}).finally(() => setLoadingInvoices(false));
  };
  // Le PDF est protégé par le token : on le récupère via authFetch puis on ouvre un blob,
  // jamais un simple <a href> (qui partirait sans en-tête d'authentification).
  const downloadInvoicePdf = async (id: string, number: string) => {
    try {
      const res = await authFetch(`/billing/invoices/${id}/pdf`, { method: "GET" });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${number}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { /* silencieux */ }
  };

  // Arrivée depuis une notification de paiement (Notifications.tsx) — même convention que
  // navigate(..., { state: { openDateSheet: true } }) déjà utilisée pour "match" : ouvre
  // directement le bon modal au lieu de laisser l'utilisateur chercher où cliquer.
  useEffect(() => {
    const state = location.state as { openPayments?: "earnings" | "transactions" } | null;
    if (state?.openPayments === "earnings") openEarningsModal();
    else if (state?.openPayments === "transactions") openTxModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const startConnectOnboarding = () => {
    if (connectLoading) return;
    setConnectLoading(true);
    const return_url = `${window.location.origin}/settings`;
    api.post("/connect/onboard", { return_url, refresh_url: return_url }).then(data => {
      if (data?.url) window.location.href = data.url;
      else if (data?.error) { alert(data.error); setConnectLoading(false); }
    }).catch(() => { alert(t(lang, "error_connection")); setConnectLoading(false); });
  };

  // Moyens de paiement — cartes enregistrées sur le Customer Stripe (voir routes/payments.ts
  // GET/POST/DELETE /payments/me/methods), jamais saisies côté serveur : "Ajouter une carte"
  // ouvre une session Checkout Stripe en mode "setup", même schéma que l'onboarding bancaire.
  const [showMethodsModal, setShowMethodsModal] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [addingMethod, setAddingMethod] = useState(false);
  const openMethodsModal = () => {
    setShowMethodsModal(true);
    setLoadingMethods(true);
    api.get("/payments/me/methods").then(data => setPaymentMethods(data?.methods || [])).catch(() => {}).finally(() => setLoadingMethods(false));
  };
  const addPaymentMethod = () => {
    if (addingMethod) return;
    setAddingMethod(true);
    const return_url = `${window.location.origin}/settings`;
    api.post("/payments/me/methods/setup", { success_url: return_url, cancel_url: return_url }).then(data => {
      if (data?.url) window.location.href = data.url;
      else { alert(data?.error || t(lang, "error_connection")); setAddingMethod(false); }
    }).catch(() => { alert(t(lang, "error_connection")); setAddingMethod(false); });
  };
  const removePaymentMethod = (id: string) => {
    api.delete(`/payments/me/methods/${id}`).then(data => {
      if (data && !data.error) setPaymentMethods(prev => prev.filter(m => m.id !== id));
      else alert(data?.error || t(lang, "error_connection"));
    }).catch(() => alert(t(lang, "error_connection")));
  };

  // Sessions actives
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [sessions, setSessions] = useState<any[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [revokingOthers, setRevokingOthers] = useState(false);
  const openSessionsModal = () => {
    setShowSessionsModal(true);
    setLoadingSessions(true);
    const current = getStoredRefreshToken() || "";
    api.get(`/users/me/sessions?current=${encodeURIComponent(current)}`).then(data => setSessions(data?.sessions || [])).catch(() => {}).finally(() => setLoadingSessions(false));
  };
  const revokeSession = (id: string) => {
    api.delete(`/users/me/sessions/${id}`).then(() => setSessions(prev => prev.filter(s => s.id !== id))).catch(() => {});
  };
  // "Déconnecter tous les autres appareils" — garde la session en cours, supprime toutes les
  // autres. Sans ça, une liste jamais nettoyée par l'utilisateur ne pouvait que grossir jusqu'à
  // expiration naturelle (7 jours) de chaque entrée.
  const revokeOtherSessions = () => {
    if (!window.confirm(t(lang,"sessions_revoke_others_confirm")||"Déconnecter tous les autres appareils ?")) return;
    setRevokingOthers(true);
    api.post("/users/me/sessions/revoke-others", { currentRefreshToken: getStoredRefreshToken() || "" })
      .then(() => setSessions(prev => prev.filter(s => s.is_current)))
      .catch(() => {})
      .finally(() => setRevokingOthers(false));
  };

  // Désactivation réversible
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const handleDeactivate = async () => {
    setDeactivating(true);
    try {
      await api.put("/users/me/deactivate", {});
      setToken(null); setUser(null); localStorage.clear();
      navigate("/login");
    } catch {
      setDeactivating(false);
    }
  };

  // Suppression de compte
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePwd, setDeletePwd] = useState("");
  const [deleteMsg, setDeleteMsg] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDeleteAccount = async () => {
    if (!deletePwd) { setDeleteMsg(t(lang, "error_confirm_password_required")); return; }
    setDeleteLoading(true);
    setDeleteMsg("");
    try {
      const res = await authFetch("/users/me", { method: "DELETE", body: JSON.stringify({ password: deletePwd }) });
      const data = await res.json();
      if (res.ok) {
        setToken(null); setUser(null); localStorage.clear();
        navigate("/login");
      } else {
        setDeleteMsg(data.error || t(lang, "error_delete_failed"));
      }
    } catch {
      setDeleteMsg(t(lang, "error_connection"));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setPwdMsg("");
    if (!curPwd || !newPwd || !confirmPwd) { setPwdMsg(t(lang, "pwd_all_fields") || "Tous les champs sont obligatoires"); return; }
    if (newPwd.length < 6) { setPwdMsg(t(lang, "pwd_too_short") || "Le nouveau mot de passe doit faire au moins 6 caractères"); return; }
    if (newPwd !== confirmPwd) { setPwdMsg(t(lang, "pwd_no_match") || "Les mots de passe ne correspondent pas"); return; }
    setPwdLoading(true);
    try {
      const res = await authFetch("/users/me/password", {
        method: "PUT",
        body: JSON.stringify({ current_password: curPwd, new_password: newPwd }),
      });
      const data = await res.json();
      if (res.ok) {
        setPwdMsg(t(lang, "pwd_success") || "Mot de passe modifié");
        setCurPwd(""); setNewPwd(""); setConfirmPwd("");
        setTimeout(() => { setShowPwdModal(false); setPwdMsg(""); }, 1500);
      } else {
        setPwdMsg(data.error || (t(lang, "pwd_error") || "Erreur"));
      }
    } catch {
      setPwdMsg(t(lang, "pwd_error") || "Erreur de connexion");
    } finally {
      setPwdLoading(false);
    }
  };
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Dérivé directement de `user` (colonnes who_can_contact/who_can_see_profile/who_can_invite,
  // valeurs 'everyone'/'friends') plutôt qu'un état local séparé — évite toute désynchronisation
  // avec la valeur réellement enregistrée en base.
  const saveWhoCan = (field: "who_can_contact" | "who_can_see_profile" | "who_can_invite", value: "everyone" | "friends") => {
    setUser({ ...user, [field]: value });
    api.put("/users/me", { [field]: value }).catch(() => {});
  };

  // Partage de position en direct — 'discret' (état actuel, ville seulement, par défaut) vs
  // 'amis' (tous mes amis) vs 'amis_choisis' (une sélection). Chargé à l'ouverture de la modale,
  // pas au montage de la page : évite un appel réseau de plus si l'utilisateur n'y touche jamais.
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationMode, setLocationMode] = useState<"discret" | "amis" | "amis_choisis" | "double_jeu">("discret");
  const [locationFriends, setLocationFriends] = useState<any[]>([]);
  const [selectedFriendIds, setSelectedFriendIds] = useState<Set<string>>(new Set());
  const [searchChosenFriends, setSearchChosenFriends] = useState("");
  const [loadingLocationSettings, setLoadingLocationSettings] = useState(false);
  const [savingLocationSettings, setSavingLocationSettings] = useState(false);
  // Double jeu — deux groupes d'amis strictement disjoints (voir routes/location.ts) + la
  // position affichée elle-même (adresse libre, pré-remplie avec l'adresse déclarée du profil
  // mais librement modifiable — sinon ce ne serait qu'un "amis_choisis" de plus, pas une vraie
  // fausse position).
  const [displayLabel, setDisplayLabel] = useState("");
  const [displayLat, setDisplayLat] = useState<number | null>(null);
  const [displayLng, setDisplayLng] = useState<number | null>(null);
  const [displayedFriendIds, setDisplayedFriendIds] = useState<Set<string>>(new Set());
  const [realFriendIds, setRealFriendIds] = useState<Set<string>>(new Set());
  const [searchDisplayed, setSearchDisplayed] = useState("");
  const [searchReal, setSearchReal] = useState("");

  useEffect(() => {
    api.get("/location/me/settings").then(data => setLocationMode(data?.mode || "discret")).catch(() => {});
  }, []);

  const openLocationModal = () => {
    setShowLocationModal(true);
    setLoadingLocationSettings(true);
    Promise.all([
      api.get("/location/me/settings"),
      api.get(`/users/${user?.id}/friends`),
    ]).then(([settings, friendsData]) => {
      setLocationMode(settings?.mode || "discret");
      setSelectedFriendIds(new Set((settings?.shared_with || []).map((f: any) => f.id)));
      setLocationFriends(friendsData?.friends || []);
      const dp = settings?.display_position;
      setDisplayLabel(dp?.label || user?.city || "");
      setDisplayLat(dp?.lat ?? (user?.latitude ? Number(user.latitude) : null));
      setDisplayLng(dp?.lng ?? (user?.longitude ? Number(user.longitude) : null));
      setDisplayedFriendIds(new Set((settings?.displayed_shared_with || []).map((f: any) => f.id)));
      setRealFriendIds(new Set((settings?.real_shared_with || []).map((f: any) => f.id)));
      setSearchDisplayed("");
      setSearchReal("");
    }).catch(() => {}).finally(() => setLoadingLocationSettings(false));
  };

  const toggleSelectedFriend = (id: string) => {
    setSelectedFriendIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Un ami ne peut être que dans un seul des deux groupes : le cocher dans l'un le retire
  // automatiquement de l'autre, jamais de doublon possible même côté écran.
  const toggleDoubleJeuFriend = (id: string, group: "displayed" | "real") => {
    const [setThis, setOther] = group === "displayed" ? [setDisplayedFriendIds, setRealFriendIds] : [setRealFriendIds, setDisplayedFriendIds];
    setThis(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setOther(prev => { if (!prev.has(id)) return prev; const next = new Set(prev); next.delete(id); return next; });
  };

  const saveLocationSettings = async () => {
    setSavingLocationSettings(true);
    try {
      const body: any = { mode: locationMode, friend_ids: Array.from(selectedFriendIds) };
      if (locationMode === "double_jeu") {
        body.display_position = { label: displayLabel, lat: displayLat, lng: displayLng };
        body.displayed_friend_ids = Array.from(displayedFriendIds);
        body.real_friend_ids = Array.from(realFriendIds);
      }
      await api.put("/location/me/settings", body);
      // Redémarre aussitôt le push de position (voir AppContext) : sans ça, activer le partage
      // n'affichait rien aux amis avant le prochain rafraîchissement de token (~10 min).
      bumpLocationSharing();
      setShowLocationModal(false);
    } catch {
      alert(t(lang, "error_connection"));
    }
    setSavingLocationSettings(false);
  };

  const LOCATION_MODE_LABELS: Record<string, string> = { discret: t(lang, "location_mode_discreet"), amis: t(lang, "location_mode_all_friends"), amis_choisis: t(lang, "location_mode_chosen_friends"), double_jeu: t(lang, "location_double_jeu_label") };

  // Préférences de notification — les MÊMES 7 catégories que côté serveur
  // (utils/notificationSettingsCategories.ts) : Messages/Appels ont leur propre case, le reste
  // reprend exactement le vocabulaire déjà connu de l'aperçu de la cloche et de l'écran
  // Notifications (Demandes/Connexions/Prestations/Paiements/Activités) — jamais une 3e
  // taxonomie à apprendre. {} du serveur = tout activé, donc `!== false` partout ci-dessous.
  const [notifPrefs, setNotifPrefs] = useState<Record<string, boolean>>({});
  const [loadingNotifPrefs, setLoadingNotifPrefs] = useState(true);
  useEffect(() => {
    api.get("/users/me/notification-preferences").then(data => setNotifPrefs(data?.preferences || {})).catch(() => {}).finally(() => setLoadingNotifPrefs(false));
  }, []);
  const toggleNotifCategory = (category: string) => {
    const next = notifPrefs[category] === false; // valeur actuelle "désactivée" -> on réactive
    setNotifPrefs(prev => ({ ...prev, [category]: next }));
    api.put("/users/me/notification-preferences", { category, enabled: next }).catch(() => {
      setNotifPrefs(prev => ({ ...prev, [category]: !next }));
    });
  };
  // Dérivé de `user`, comme who_can_contact — pas d'état local séparé.
  const toggleTwoFA = (v: boolean) => {
    setUser({ ...user, two_factor_enabled: v });
    api.put("/users/me/2fa", { enabled: v }).catch(() => setUser({ ...user, two_factor_enabled: !v }));
  };

  const currentLang = LANGS.find(l => l.code === lang) || LANGS[0];

  const card: React.CSSProperties = { backgroundColor: "white", borderRadius: 16, overflow: "hidden", marginBottom: 4, border: "1px solid #F0F0F0" };

  return (
    <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", minHeight: "calc(100vh - 60px)", overflow: isMobile ? "visible" : "hidden" }}>
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ maxWidth: 700, margin: "0 auto", padding: "0 20px 40px" }}>

          {/* Profil card */}
          <div onClick={() => navigate("/profile")} style={{ display: "flex", alignItems: "center", gap: 14, backgroundColor: "white", borderRadius: 16, padding: 16, margin: "20px 0 4px", border: "1px solid #F0F0F0", cursor: "pointer" }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", backgroundColor: "#0a0a0a", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 18 }}>{user?.avatar_url ? <img src={user.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : sInitials}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{sName}</div>
              <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>{t(lang, "see_edit_profile")}</div>
            </div>
            <span style={{ color: "#ccc", fontSize: 18 }}>›</span>
          </div>

          {/* Langue */}
          <SectionTitle title={t(lang,"settings_language")||"Langue"} />
          {/* overflow:"visible" en dur : `card` impose overflow:hidden (pour les coins arrondis),
              ce qui rognait tout le popover de LanguagePicker (position:absolute) dès qu'il
              s'ouvrait — le clic fonctionnait bien, mais aucune autre langue n'était visible. */}
          <div style={{ ...card, overflow: "visible", padding: "13px 20px", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{t(lang, "settings_app_language")}</div>
              <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{currentLang.label}</div>
            </div>
            <LanguagePicker lang={lang} setLang={setLang} size={32} align="right" />
          </div>

          {/* Compte */}
          <SectionTitle title={t(lang,"settings_account")||"Compte"} />
          <div style={card}>
            <SettingItem icon={User} label={t(lang, "settings_account_info")} sub={t(lang, "settings_account_info_sub")} onPress={() => navigate("/account")} />
            <SettingItem icon={UserPlus} label={t(lang, "settings_invite_friends")} sub={t(lang, "settings_invite_friends_sub")} onPress={() => navigate("/invite")} />
            <SettingItem icon={Lock} label={t(lang, "change_password") || "Changer le mot de passe"} onPress={() => setShowPwdModal(true)} />
            <SettingItem icon={ShieldCheck} label={t(lang, "settings_identity_verification")} sub={user?.is_verified ? t(lang, "verified_profile_check") : t(lang, "request_verified_badge")} onPress={() => navigate("/verify-identity")} />
            <SettingItem icon={FileText} label={t(lang, "vfy_settings_add_doc")} sub={t(lang, "vfy_settings_add_doc_sub")} onPress={() => navigate("/verify-identity")} />
            <SettingItem icon={Calendar} label={t(lang, "settings_my_bookings")} sub={t(lang, "settings_my_bookings_sub")} onPress={() => navigate("/bookings")} />
            <SettingItem icon={Megaphone} label={t(lang, "side_menu_ad_tool")} sub={t(lang, "side_menu_ad_tool_sub")} onPress={() => navigate("/ad-tool")} />
          </div>

          {/* Confidentialité */}
          <SectionTitle title={t(lang,"settings_privacy")||"Confidentialité"} />
          <div style={card}>
            {/* "Compte privé" — même champ que who_can_see_profile juste en dessous (une demande
                d'ami exige déjà un double consentement chez OLTODO, donc 'friends' EST la version
                privée), remonté en tête sous une bascule simple plutôt qu'un sélecteur à deux
                options, pour la rendre aussi visible que chez Instagram/BeReal. */}
            <ToggleItem
              icon={Eye}
              label={t(lang, "settings_private_account") || "Compte privé"}
              sub={t(lang, "settings_private_account_sub") || "Seuls tes amis voient ton profil"}
              value={user?.who_can_see_profile === "friends"}
              onToggle={() => saveWhoCan("who_can_see_profile", user?.who_can_see_profile === "friends" ? "everyone" : "friends")}
            />
            <SegmentedItem icon={MessageCircle} label={t(lang, "settings_who_contact")} value={user?.who_can_contact === "friends" ? "friends" : "everyone"} onChange={v => saveWhoCan("who_can_contact", v)} lang={lang} />
            <SegmentedItem icon={Send} label={t(lang, "settings_who_invite")} value={user?.who_can_invite === "friends" ? "friends" : "everyone"} onChange={v => saveWhoCan("who_can_invite", v)} lang={lang} />
            <SettingItem icon={MapPin} label={t(lang, "settings_location_sharing")} sub={LOCATION_MODE_LABELS[locationMode]} onPress={openLocationModal} />
            <SettingItem icon={UserX} label={t(lang, "settings_blocked_users")} onPress={openBlockedModal} />
            <SettingItem icon={EyeOff} label={t(lang, "settings_hidden_users") || "Utilisateurs masqués"} onPress={openHiddenModal} />
          </div>

          {/* Notifications — une catégorie désactivée coupe seulement le push (vibreur/son) ;
              la notification reste toujours créée et visible dans l'app (voir notifyUser côté
              serveur), jamais cachée. */}
          <SectionTitle title={t(lang,"settings_notifications")||"Notifications"} />
          <div style={{ ...card, opacity: loadingNotifPrefs ? 0.5 : 1, pointerEvents: loadingNotifPrefs ? "none" : "auto" }}>
            <ToggleItem icon={MessageCircle} label={t(lang, "notif_cat_messages")} sub={t(lang, "notif_cat_messages_sub")} value={notifPrefs.messages !== false} onToggle={() => toggleNotifCategory("messages")} />
            <ToggleItem icon={Eye} label={t(lang, "notif_message_preview")} sub={t(lang, "notif_message_preview_sub")} value={notifPrefs.message_preview !== false} onToggle={() => toggleNotifCategory("message_preview")} />
            <ToggleItem icon={Phone} label={t(lang, "notif_cat_calls")} sub={t(lang, "notif_cat_calls_sub")} value={notifPrefs.calls !== false} onToggle={() => toggleNotifCategory("calls")} />
            <ToggleItem icon={UserPlus} label={t(lang, "notif_tab_requests")} sub={t(lang, "notif_cat_demandes_sub")} value={notifPrefs.demandes !== false} onToggle={() => toggleNotifCategory("demandes")} />
            <ToggleItem icon={Heart} label={t(lang, "notif_tab_connections")} sub={t(lang, "notif_cat_connexions_sub")} value={notifPrefs.connexions !== false} onToggle={() => toggleNotifCategory("connexions")} />
            <ToggleItem icon={Briefcase} label={t(lang, "notif_tab_prestations")} sub={t(lang, "notif_cat_prestations_sub")} value={notifPrefs.prestations !== false} onToggle={() => toggleNotifCategory("prestations")} />
            <ToggleItem icon={Euro} label={t(lang, "notif_tab_payments")} sub={t(lang, "notif_cat_paiements_sub")} value={notifPrefs.paiements !== false} onToggle={() => toggleNotifCategory("paiements")} />
            <ToggleItem icon={Bell} label={t(lang, "notif_tab_activities")} sub={t(lang, "notif_cat_activites_sub")} value={notifPrefs.activites !== false} onToggle={() => toggleNotifCategory("activites")} />
          </div>

          {/* Paiements */}
          <SectionTitle title={t(lang,"settings_payments")||"Paiements"} />
          <div style={card}>
            <SettingItem icon={CreditCard} label={t(lang, "settings_payment_methods")} sub={t(lang, "settings_payment_methods_sub")} onPress={openMethodsModal} />
            <SettingItem icon={Receipt} label={t(lang, "settings_transaction_history")} onPress={openTxModal} />
            <SettingItem icon={TrendingUp} label={t(lang, "settings_my_earnings")} sub={t(lang, "settings_my_earnings_sub")} onPress={openEarningsModal} />
            <SettingItem icon={Building2} label={t(lang, "billing_identity_title")} sub={t(lang, "billing_identity_sub")} onPress={openBillingModal} />
            <SettingItem icon={FileText} label={t(lang, "invoices_title")} sub={t(lang, "invoices_sub")} onPress={openInvoicesModal} />
            <SettingItem
              icon={Landmark}
              label={t(lang, "settings_bank_account")}
              sub={connectStatus?.payouts_enabled ? t(lang, "bank_connected") : connectStatus?.onboarded ? t(lang, "bank_verifying") : t(lang, "bank_setup_prompt")}
              onPress={startConnectOnboarding}
            />
          </div>

          {/* Sécurité */}
          <SectionTitle title={t(lang,"settings_security")||"Sécurité"} />
          <div style={card}>
            <ToggleItem icon={Fingerprint} label={t(lang, "settings_2fa")} sub={t(lang, "settings_2fa_sub")} value={!!user?.two_factor_enabled} onToggle={() => toggleTwoFA(!user?.two_factor_enabled)} />
            <SettingItem icon={Smartphone} label={t(lang, "settings_active_sessions")} sub={t(lang, "settings_active_sessions_sub")} onPress={openSessionsModal} />
            <SettingItem icon={PauseCircle} label={t(lang, "settings_deactivate_account")} sub={t(lang, "settings_deactivate_sub")} onPress={() => setShowDeactivateModal(true)} />
            <SettingItem icon={Trash2} label={t(lang, "settings_delete_account")} sub={t(lang, "irreversible_action")} onPress={() => { setDeletePwd(""); setDeleteMsg(""); setShowDeleteModal(true); }} />
          </div>

          {/* À propos */}
          <SectionTitle title={t(lang,"settings_about")||"À propos"} />
          <div style={card}>
            <SettingItem icon={Scale} label={t(lang, "settings_legal_center") || "Centre des règles"} sub={t(lang, "settings_legal_center_sub") || "CGU, confidentialité, paiements, cookies"} onPress={() => navigate("/legal")} />
            <SettingItem icon={BookOpen} label={t(lang, "settings_help_guide")} onPress={() => navigate("/help")} />
            <SettingItem icon={HelpCircle} label={t(lang, "settings_support")} onPress={() => navigate("/support")} />
            <SettingItem icon={Info} label={t(lang,"settings_version")||"Version"} right={<span style={{ fontSize: 13, color: "#999" }}>1.0.0</span>} />
          </div>

          {/* Déconnexion */}
          <div onClick={() => { setToken(null); setUser(null); localStorage.clear(); navigate("/login"); }} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, margin: "16px 0", padding: "14px", borderRadius: 14, border: "1px solid #E0E0E0", backgroundColor: "#F5F5F5", cursor: "pointer" }}>
            <LogOut size={17} strokeWidth={1.75} color="#0a0a0a" />
            <span style={{ fontSize: 15, color: "#0a0a0a", fontWeight: 600 }}>{t(lang,"settings_logout")||"Se déconnecter"}</span>
          </div>

        </div>
      </div>

      {/* Sidebar */}
      <div style={{ display: isMobile ? "none" : "block", width: 280, flexShrink: 0, borderLeft: "1px solid #F0F0F0", padding: 20, overflowY: "auto" }}>
        <div style={{ backgroundColor: "white", borderRadius: 16, padding: 16, border: "1px solid #F0F0F0", marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{t(lang, "profile_label")}</div>
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <div style={{ width: 60, height: 60, borderRadius: "50%", backgroundColor: "#0a0a0a", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 20, margin: "0 auto 10px" }}>{user?.avatar_url ? <img src={user.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : sInitials}</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{sName}</div>
            <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>{sHandle}{sCity ? " · " + sCity : ""}</div>
            {user?.is_verified && (
              <div style={{ display: "flex", justifyContent: "center", gap: 4, marginTop: 8 }}>
                <span style={{ fontSize: 12, color: "#0a0a0a", fontWeight: 600 }}>{t(lang, "verified_badge")}</span>
              </div>
            )}
          </div>
        </div>

        <div style={{ backgroundColor: "white", borderRadius: 16, padding: 16, border: "1px solid #F0F0F0", textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{t(lang, "active_language")}</div>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
            <LanguagePicker lang={lang} setLang={setLang} size={48} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{currentLang.label}</div>
        </div>
      </div>

      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
          position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
          "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
          vrai viewport. */}
      {showPwdModal && ReactDOM.createPortal(
        <div onClick={() => setShowPwdModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 16, padding: 24, width: "100%", maxWidth: 380 }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>{t(lang, "change_password") || "Changer le mot de passe"}</div>
            <input type="password" placeholder={t(lang, "pwd_current") || "Mot de passe actuel"} value={curPwd} onChange={e => setCurPwd(e.target.value)} style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "#F8F8F8", fontSize: 14, outline: "none", marginBottom: 10, boxSizing: "border-box" }} />
            <input type="password" placeholder={t(lang, "pwd_new") || "Nouveau mot de passe"} value={newPwd} onChange={e => setNewPwd(e.target.value)} style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "#F8F8F8", fontSize: 14, outline: "none", marginBottom: 10, boxSizing: "border-box" }} />
            <input type="password" placeholder={t(lang, "pwd_confirm") || "Confirmer le nouveau mot de passe"} value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "#F8F8F8", fontSize: 14, outline: "none", marginBottom: 10, boxSizing: "border-box" }} />
            {pwdMsg && <div style={{ fontSize: 13, color: pwdMsg === (t(lang, "pwd_success") || "Mot de passe modifié") ? "#0a0a0a" : "#0a0a0a", marginBottom: 10 }}>{pwdMsg}</div>}
            <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
              <button onClick={() => setShowPwdModal(false)} style={{ flex: 1, padding: "11px", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "white", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>{t(lang, "cancel") || "Annuler"}</button>
              <button onClick={handleChangePassword} disabled={pwdLoading} style={{ flex: 1, padding: "11px", borderRadius: 10, border: "none", backgroundColor: "#0a0a0a", color: "white", cursor: "pointer", fontSize: 14, fontWeight: 600, opacity: pwdLoading ? 0.6 : 1 }}>{pwdLoading ? "..." : (t(lang, "save") || "Enregistrer")}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
          position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
          "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
          vrai viewport. */}
      {showTxModal && ReactDOM.createPortal(
        <div onClick={() => setShowTxModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 16, padding: 24, width: "100%", maxWidth: 440, maxHeight: "80vh", overflowY: "auto", boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <span style={{ fontSize: 18, fontWeight: 700 }}>{t(lang, "settings_transaction_history")}</span>
              <button onClick={() => setShowTxModal(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>
            {loadingTx ? (
              <p style={{ fontSize: 13, color: "#999" }}>{t(lang, "loading_ellipsis")}</p>
            ) : transactions.length === 0 ? (
              <p style={{ fontSize: 13, color: "#999" }}>{t(lang, "no_transactions_yet")}</p>
            ) : (
              transactions.map(tx => (
                <div key={tx.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #F0F0F0" }}>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>{tx.target_label || t(lang, "transaction_label")}</div>
                    <div style={{ fontSize: 11.5, color: "#999", marginTop: 2 }}>{new Date(tx.created_at).toLocaleDateString("fr-FR")} · {txStatusLabel(lang, tx.status)}</div>
                    {tx.receipt_invoice_id && (
                      <button onClick={() => downloadInvoicePdf(tx.receipt_invoice_id, "recu")} style={{ marginTop: 3, border: "none", background: "none", padding: 0, cursor: "pointer", color: "#0a0a0a", fontSize: 11.5, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                        <Download size={12} /> {t(lang, "invoice_download_receipt")}
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{Number(tx.amount).toFixed(2)} {(tx.currency || "eur").toUpperCase()}</div>
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
          position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
          "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
          vrai viewport. */}
      {showEarningsModal && ReactDOM.createPortal(
        <div onClick={() => setShowEarningsModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 16, padding: 24, width: "100%", maxWidth: 460, maxHeight: "85vh", overflowY: "auto", boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <span style={{ fontSize: 18, fontWeight: 700 }}>{t(lang, "settings_my_earnings")}</span>
              <button onClick={() => setShowEarningsModal(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>
            {loadingEarnings ? (
              <p style={{ fontSize: 13, color: "#999" }}>{t(lang, "loading_ellipsis")}</p>
            ) : (
              <>
                {[
                  { key: "in_escrow", label: t(lang, "earnings_in_escrow"), always: true },
                  { key: "awaiting_payout_setup", label: t(lang, "earnings_awaiting_account"), always: false },
                  { key: "disputed", label: t(lang, "tx_status_disputed"), always: false },
                ].filter(b => b.always || Number(earningsStats?.[b.key] || 0) > 0).map((b, i, arr) => (
                  <div key={b.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: i < arr.length - 1 ? "1px solid #F0F0F0" : "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{ fontSize: 13, color: "#999" }}>{b.label}</span>
                      {b.key === "in_escrow" && (
                        <button onClick={() => alert(t(lang, "earnings_escrow_info"))} title={t(lang, "earnings_escrow_info")} style={{ border: "none", background: "none", cursor: "pointer", padding: 0, display: "flex", color: "#999" }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><line x1="12" y1="11" x2="12" y2="16" /><circle cx="12" cy="8" r="0.5" fill="currentColor" /></svg>
                        </button>
                      )}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#0a0a0a" }}>{Number(earningsStats?.[b.key] || 0).toFixed(2)} €</span>
                  </div>
                ))}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "22px 0", marginTop: 6, borderTop: "1px solid #F0F0F0", borderBottom: "1px solid #F0F0F0", marginBottom: 16 }}>
                  <span style={{ fontSize: 34, fontWeight: 800, color: "#0a0a0a", letterSpacing: -0.5 }}>
                    {Number(earningsStats?.released || 0).toFixed(2)} €
                  </span>
                  <span style={{ fontSize: 12.5, color: "#999", marginTop: 4 }}>{t(lang, "earnings_released")}</span>
                </div>
                {!connectStatus?.payouts_enabled && (
                  <div style={{ fontSize: 12, color: "#999", backgroundColor: "#F8F8F8", borderRadius: 10, padding: 10, marginBottom: 16 }}>
                    {t(lang, "earnings_bank_warning")}
                  </div>
                )}
                {earningsRows.length > 0 && (
                  <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, color: "#999", marginBottom: 8 }}>{t(lang, "settings_transaction_history")}</div>
                )}
                {earningsRows.length === 0 ? (
                  <p style={{ fontSize: 13, color: "#999" }}>{t(lang, "no_earnings_yet")}</p>
                ) : (
                  earningsRows.map(row => (
                    <div key={row.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #F0F0F0" }}>
                      <div>
                        <div style={{ fontSize: 13.5, fontWeight: 600 }}>{row.target_label || t(lang, "transaction_label")}</div>
                        <div style={{ fontSize: 11.5, color: "#999", marginTop: 2 }}>{new Date(row.created_at).toLocaleDateString("fr-FR")} · {txStatusLabel(lang, row.status)}</div>
                        {row.commission_invoice_id && (
                          <button onClick={() => downloadInvoicePdf(row.commission_invoice_id, "commission")} style={{ marginTop: 3, border: "none", background: "none", padding: 0, cursor: "pointer", color: "#0a0a0a", fontSize: 11.5, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                            <Download size={12} /> {t(lang, "invoice_download_commission")}
                          </button>
                        )}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{(Number(row.amount) - Number(row.platform_fee_amount || 0)).toFixed(2)} €</div>
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Identité de facturation — modèle d'intermédiaire transparent : cette identité sert aux
          reçus émis au nom du vendeur ET aux factures de commission d'OLTODO. Une seule saisie,
          réutilisée pour tous les lieux/prestations du compte. */}
      {showBillingModal && ReactDOM.createPortal(
        <div onClick={() => setShowBillingModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 16, padding: 24, width: "100%", maxWidth: 460, maxHeight: "85vh", overflowY: "auto", boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 18, fontWeight: 700 }}>{t(lang, "billing_identity_title")}</span>
              <button onClick={() => setShowBillingModal(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>
            <p style={{ fontSize: 12, color: "#999", marginBottom: 16 }}>{t(lang, "billing_identity_sub")}</p>
            {[
              { k: "legal_name", label: t(lang, "billing_legal_name") },
              { k: "legal_form", label: t(lang, "billing_legal_form") },
              { k: "siren", label: t(lang, "billing_siren") },
              { k: "siret", label: t(lang, "billing_siret") },
              { k: "billing_address", label: t(lang, "billing_address") },
              { k: "billing_postal_code", label: t(lang, "settings_postal_code") || "Code postal" },
              { k: "billing_city", label: t(lang, "settings_city") || "Ville" },
            ].map(f => (
              <div key={f.k} style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11.5, fontWeight: 700, color: "#999", display: "block", marginBottom: 5 }}>{f.label}</label>
                <input value={billing[f.k] || ""} onChange={e => setBilling((b: any) => ({ ...b, [f.k]: e.target.value }))} style={{ width: "100%", border: "1px solid #E0E0E0", borderRadius: 10, padding: "10px 12px", fontSize: 14, boxSizing: "border-box" }} />
              </div>
            ))}
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0" }}>
              <div style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{t(lang, "billing_vat_toggle")}</div>
              <Toggle value={!!billing.vat_applicable} onChange={() => setBilling((b: any) => ({ ...b, vat_applicable: !b.vat_applicable }))} />
            </div>
            {billing.vat_applicable && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 11.5, fontWeight: 700, color: "#999", display: "block", marginBottom: 5 }}>{t(lang, "billing_vat_number")}</label>
                  <input value={billing.vat_number || ""} onChange={e => setBilling((b: any) => ({ ...b, vat_number: e.target.value }))} style={{ width: "100%", border: "1px solid #E0E0E0", borderRadius: 10, padding: "10px 12px", fontSize: 14, boxSizing: "border-box" }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 11.5, fontWeight: 700, color: "#999", display: "block", marginBottom: 5 }}>{t(lang, "billing_vat_rate")}</label>
                  <input type="number" value={billing.vat_rate ?? 20} onChange={e => setBilling((b: any) => ({ ...b, vat_rate: Number(e.target.value) }))} style={{ width: "100%", border: "1px solid #E0E0E0", borderRadius: 10, padding: "10px 12px", fontSize: 14, boxSizing: "border-box" }} />
                </div>
              </>
            )}
            {billingErr && <p style={{ fontSize: 12.5, color: "#c0392b", marginTop: 4 }}>{billingErr}</p>}
            <button onClick={saveBilling} disabled={savingBilling} style={{ width: "100%", padding: "13px", borderRadius: 10, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer", marginTop: 12, opacity: savingBilling ? 0.6 : 1 }}>
              {savingBilling ? t(lang, "loading_ellipsis") : t(lang, "billing_save")}
            </button>
          </div>
        </div>,
        document.body
      )}

      {showInvoicesModal && ReactDOM.createPortal(
        <div onClick={() => setShowInvoicesModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 16, padding: 24, width: "100%", maxWidth: 460, maxHeight: "85vh", overflowY: "auto", boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <span style={{ fontSize: 18, fontWeight: 700 }}>{t(lang, "invoices_title")}</span>
              <button onClick={() => setShowInvoicesModal(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>
            {loadingInvoices ? (
              <p style={{ fontSize: 13, color: "#999" }}>{t(lang, "loading_ellipsis")}</p>
            ) : invoices.length === 0 ? (
              <p style={{ fontSize: 13, color: "#999" }}>{t(lang, "invoices_empty")}</p>
            ) : (
              invoices.map(inv => (
                <div key={inv.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #F0F0F0" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{inv.number}</div>
                    <div style={{ fontSize: 11.5, color: "#999", marginTop: 2 }}>
                      {inv.kind === "commission" ? t(lang, "invoice_kind_commission") : t(lang, "invoice_kind_receipt")} · {new Date(inv.issued_at).toLocaleDateString("fr-FR")} · {Number(inv.total_ttc).toFixed(2)} €
                    </div>
                  </div>
                  <button onClick={() => downloadInvoicePdf(inv.id, inv.number)} style={{ border: "1px solid #E0E0E0", background: "white", borderRadius: 8, padding: "6px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#0a0a0a", flexShrink: 0, display: "flex", alignItems: "center", gap: 5 }}>
                    <Download size={13} /> PDF
                  </button>
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
          position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
          "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
          vrai viewport. */}
      {showMethodsModal && ReactDOM.createPortal(
        <div onClick={() => setShowMethodsModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 16, padding: 24, width: "100%", maxWidth: 440, maxHeight: "80vh", overflowY: "auto", boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <span style={{ fontSize: 18, fontWeight: 700 }}>{t(lang, "settings_payment_methods")}</span>
              <button onClick={() => setShowMethodsModal(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>
            {loadingMethods ? (
              <p style={{ fontSize: 13, color: "#999" }}>{t(lang, "loading_ellipsis")}</p>
            ) : paymentMethods.length === 0 ? (
              <p style={{ fontSize: 13, color: "#999" }}>{t(lang, "no_payment_methods")}</p>
            ) : (
              paymentMethods.map(m => (
                <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #F0F0F0" }}>
                  <CreditCard size={17} strokeWidth={1.75} color="#999" style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>{(m.brand || "").toUpperCase()} •••• {m.last4}</div>
                    <div style={{ fontSize: 11.5, color: "#999", marginTop: 2 }}>{t(lang, "card_expires_prefix")} {String(m.exp_month).padStart(2, "0")}/{m.exp_year}</div>
                  </div>
                  <button onClick={() => removePaymentMethod(m.id)} style={{ border: "1px solid #E0E0E0", background: "white", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#0a0a0a", flexShrink: 0 }}>{t(lang, "revoke")}</button>
                </div>
              ))
            )}
            <button onClick={addPaymentMethod} disabled={addingMethod} style={{ width: "100%", padding: "13px", borderRadius: 10, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer", marginTop: 16, opacity: addingMethod ? 0.6 : 1 }}>
              {addingMethod ? t(lang, "loading_ellipsis") : t(lang, "add_payment_method")}
            </button>
          </div>
        </div>,
        document.body
      )}

      {showSessionsModal && ReactDOM.createPortal(
        <div onClick={() => setShowSessionsModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 16, padding: 24, width: "100%", maxWidth: 440, maxHeight: "80vh", overflowY: "auto", boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <span style={{ fontSize: 18, fontWeight: 700 }}>{t(lang, "settings_active_sessions")}</span>
              <button onClick={() => setShowSessionsModal(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>
            {loadingSessions ? (
              <p style={{ fontSize: 13, color: "#999" }}>{t(lang, "loading_ellipsis")}</p>
            ) : sessions.length === 0 ? (
              <p style={{ fontSize: 13, color: "#999" }}>{t(lang, "no_active_sessions")}</p>
            ) : (
              <>
                {sessions.map(s => (
                  <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #F0F0F0" }}>
                    <Smartphone size={17} strokeWidth={1.75} color="#999" style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                        {s.device_name || t(lang, "unknown_device")}
                        {s.is_current && <span style={{ fontSize: 10.5, fontWeight: 700, color: "#0a0a0a", backgroundColor: "#F0F0F0", borderRadius: 8, padding: "2px 7px" }}>{t(lang,"sessions_this_device")||"Cet appareil"}</span>}
                      </div>
                      <div style={{ fontSize: 11.5, color: "#999", marginTop: 2 }}>{s.location || t(lang, "unknown_location")} · {t(lang, "session_opened_prefix")} {new Date(s.created_at).toLocaleDateString()}</div>
                    </div>
                    {/* Pas de bouton "Révoquer" sur sa propre session en cours : la supprimer ici
                        ne déconnecte pas réellement l'onglet ouvert, juste la ligne en base — état
                        confus évité en ne proposant pas l'action à cet endroit précis. */}
                    {!s.is_current && (
                      <button onClick={() => revokeSession(s.id)} style={{ border: "1px solid #E0E0E0", background: "white", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#c0392b", flexShrink: 0 }}>{t(lang, "revoke")}</button>
                    )}
                  </div>
                ))}
                {sessions.length > 1 && (
                  <button onClick={revokeOtherSessions} disabled={revokingOthers} style={{ width: "100%", padding: "12px", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "white", color: "#c0392b", fontSize: 13.5, fontWeight: 600, cursor: "pointer", marginTop: 16, opacity: revokingOthers ? 0.6 : 1 }}>
                    {revokingOthers ? t(lang,"loading_ellipsis") : (t(lang,"sessions_revoke_others")||"Déconnecter tous les autres appareils")}
                  </button>
                )}
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
          position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
          "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
          vrai viewport. */}
      {showDeactivateModal && ReactDOM.createPortal(
        <div onClick={() => setShowDeactivateModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 16, padding: 24, width: "100%", maxWidth: 400 }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{t(lang, "deactivate_confirm_title")}</div>
            <p style={{ fontSize: 13.5, color: "#666", lineHeight: 1.6, marginBottom: 16 }}>
              {t(lang, "deactivate_confirm_body")}
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setShowDeactivateModal(false)} style={{ flex: 1, padding: "11px", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "white", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>{t(lang, "cancel")}</button>
              <button onClick={handleDeactivate} disabled={deactivating} style={{ flex: 1, padding: "11px", borderRadius: 10, border: "none", backgroundColor: "#0a0a0a", color: "white", cursor: "pointer", fontSize: 14, fontWeight: 600, opacity: deactivating ? 0.6 : 1 }}>{deactivating ? "..." : t(lang, "deactivate_confirm_button")}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
          position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
          "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
          vrai viewport. */}
      {showDeleteModal && ReactDOM.createPortal(
        <div onClick={() => setShowDeleteModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 16, padding: 24, width: "100%", maxWidth: 400 }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{t(lang, "delete_confirm_title")}</div>
            <p style={{ fontSize: 13.5, color: "#666", lineHeight: 1.6, marginBottom: 16 }}>
              {t(lang, "delete_confirm_body")}
            </p>
            <input
              type="password" placeholder={t(lang, "password_label")} value={deletePwd} onChange={e => setDeletePwd(e.target.value)}
              style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "#F8F8F8", fontSize: 14, outline: "none", marginBottom: 10, boxSizing: "border-box" }}
            />
            {deleteMsg && <div style={{ fontSize: 13, color: "#c0392b", marginBottom: 10 }}>{deleteMsg}</div>}
            <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
              <button onClick={() => setShowDeleteModal(false)} style={{ flex: 1, padding: "11px", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "white", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>{t(lang, "cancel")}</button>
              <button onClick={handleDeleteAccount} disabled={deleteLoading} style={{ flex: 1, padding: "11px", borderRadius: 10, border: "none", backgroundColor: "#c0392b", color: "white", cursor: "pointer", fontSize: 14, fontWeight: 600, opacity: deleteLoading ? 0.6 : 1 }}>{deleteLoading ? "..." : t(lang, "delete_permanently_button")}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showHiddenModal && ReactDOM.createPortal(
        <div onClick={() => setShowHiddenModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 16, padding: 24, width: "100%", maxWidth: 420, maxHeight: "75vh", overflowY: "auto", boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 18, fontWeight: 700 }}>{t(lang, "settings_hidden_users") || "Utilisateurs masqués"}</span>
              <button onClick={() => setShowHiddenModal(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>
            <p style={{ fontSize: 12.5, color: "#999", marginBottom: 16, lineHeight: 1.5 }}>
              {t(lang, "settings_hidden_users_sub") || "Ces personnes ne voient pas qu'elles sont masquées et peuvent toujours te contacter, seul leur contenu disparaît de ton fil."}
            </p>
            {loadingHidden && <div style={{ textAlign: "center", color: "#999", fontSize: 13, padding: "20px 0" }}>{t(lang, "loading_ellipsis")}</div>}
            {!loadingHidden && hiddenUsers.length === 0 && (
              <div style={{ textAlign: "center", color: "#999", fontSize: 13, padding: "20px 0" }}>{t(lang, "no_hidden_users") || "Aucun utilisateur masqué"}</div>
            )}
            {hiddenUsers.map(u => (
              <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #F5F5F5" }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: "#0a0a0a", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                  {u.avatar_url ? <img src={u.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (u.name || "?").split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600 }}>{u.name}</div>
                <button onClick={() => unhideUser(u.id)} style={{ padding: "7px 14px", borderRadius: 16, border: "1px solid #E0E0E0", backgroundColor: "white", color: "#0a0a0a", fontSize: 12.5, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}>{t(lang, "unhide") || "Réafficher"}</button>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}

      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
          position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
          "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
          vrai viewport. */}
      {showBlockedModal && ReactDOM.createPortal(
        <div onClick={() => setShowBlockedModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 16, padding: 24, width: "100%", maxWidth: 420, maxHeight: "75vh", overflowY: "auto", boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <span style={{ fontSize: 18, fontWeight: 700 }}>{t(lang, "settings_blocked_users")}</span>
              <button onClick={() => setShowBlockedModal(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>
            {loadingBlocked && <div style={{ textAlign: "center", color: "#999", fontSize: 13, padding: "20px 0" }}>{t(lang, "loading_ellipsis")}</div>}
            {!loadingBlocked && blockedUsers.length === 0 && (
              <div style={{ textAlign: "center", color: "#999", fontSize: 13, padding: "20px 0" }}>{t(lang, "no_blocked_users")}</div>
            )}
            {blockedUsers.map(u => (
              <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #F5F5F5" }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: "#0a0a0a", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                  {u.avatar_url ? <img src={u.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (u.name || "?").split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600 }}>{u.name}</div>
                <button onClick={() => unblockUser(u.id)} style={{ padding: "7px 14px", borderRadius: 16, border: "1px solid #E0E0E0", backgroundColor: "white", color: "#0a0a0a", fontSize: 12.5, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}>{t(lang, "unblock")}</button>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}

      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
          position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
          "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
          vrai viewport. */}
      {showLocationModal && ReactDOM.createPortal(
        <div onClick={() => setShowLocationModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 16, padding: 24, width: "100%", maxWidth: 440, maxHeight: "85vh", overflowY: "auto", boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 18, fontWeight: 700 }}>{t(lang, "settings_location_sharing")}</span>
              <button onClick={() => setShowLocationModal(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>
            <p style={{ fontSize: 13, color: "#999", marginBottom: 18, lineHeight: 1.6 }}>
              {t(lang, "location_modal_desc")}
            </p>

            {loadingLocationSettings ? (
              <div style={{ textAlign: "center", color: "#999", fontSize: 13, padding: "20px 0" }}>{t(lang, "loading_ellipsis")}</div>
            ) : (
              <>
                {([
                  { value: "discret", label: t(lang, "location_mode_discreet"), sub: t(lang, "location_mode_discreet_sub") },
                  { value: "amis", label: t(lang, "location_mode_all_friends"), sub: t(lang, "location_mode_all_friends_sub") },
                  { value: "amis_choisis", label: t(lang, "location_mode_chosen_friends"), sub: t(lang, "location_mode_chosen_friends_sub") },
                  { value: "double_jeu", label: t(lang, "location_double_jeu_label") || "Double jeu", sub: t(lang, "location_double_jeu_sub") || "Gérez deux positions indépendantes, chacune visible par un groupe d'amis différent." },
                ] as const).map(opt => (
                  <div key={opt.value} onClick={() => setLocationMode(opt.value)} style={{
                    display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 14px", borderRadius: 12, cursor: "pointer", marginBottom: 8,
                    border: locationMode === opt.value ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0",
                    backgroundColor: locationMode === opt.value ? "#F5F5F5" : "white",
                  }}>
                    <div style={{ width: 18, height: 18, borderRadius: 9, border: `2px solid ${locationMode === opt.value ? "#0a0a0a" : "#ccc"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                      {locationMode === opt.value && <div style={{ width: 9, height: 9, borderRadius: 5, backgroundColor: "#0a0a0a" }} />}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{opt.label}</div>
                      <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>{opt.sub}</div>
                    </div>
                  </div>
                ))}

                {locationMode === "amis_choisis" && (
                  <div style={{ marginTop: 12, borderTop: "1px solid #F0F0F0", paddingTop: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#666", marginBottom: 10 }}>{t(lang, "who_can_see_position")}</div>
                    <DoubleJeuFriendGroup
                      accent="#0a0a0a" accentSoft="#F5F5F5"
                      friends={locationFriends} selectedIds={selectedFriendIds} otherIds={EMPTY_ID_SET} otherLabel=""
                      onToggle={toggleSelectedFriend}
                      search={searchChosenFriends} onSearchChange={setSearchChosenFriends}
                      lang={lang}
                    />
                  </div>
                )}

                {locationMode === "double_jeu" && (
                  <div style={{ marginTop: 12, borderTop: "1px solid #F0F0F0", paddingTop: 14 }}>
                    {/* Position affichée — une vraie fausse position, pré-remplie avec l'adresse
                        déclarée du profil mais librement modifiable via la même recherche
                        d'adresse que la création de lieu. */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <XCircle size={17} strokeWidth={2} color="#DC2626" />
                      <span style={{ fontSize: 13.5, fontWeight: 700 }}>{t(lang, "displayed_position_label")}</span>
                    </div>
                    <AddressSearch value={displayLabel} onChange={(label, lat, lng) => { setDisplayLabel(label); setDisplayLat(lat); setDisplayLng(lng); }} placeholder={t(lang, "displayed_position_placeholder")} />
                    <div style={{ marginTop: 14 }}>
                      <DoubleJeuFriendGroup
                        accent="#DC2626" accentSoft="#FEF2F2"
                        friends={locationFriends} selectedIds={displayedFriendIds} otherIds={realFriendIds} otherLabel={t(lang, "in_real_position")}
                        onToggle={(id) => toggleDoubleJeuFriend(id, "displayed")}
                        search={searchDisplayed} onSearchChange={setSearchDisplayed} lang={lang}
                      />
                    </div>

                    {/* Position réelle — le direct GPS existant, juste réservé à ce second
                        groupe, strictement disjoint du premier. */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "22px 0 10px", borderTop: "1px solid #F0F0F0", paddingTop: 16 }}>
                      <BadgeCheck size={17} strokeWidth={2} color="#16A34A" />
                      <span style={{ fontSize: 13.5, fontWeight: 700 }}>{t(lang, "real_position_label")}</span>
                    </div>
                    <DoubleJeuFriendGroup
                      accent="#16A34A" accentSoft="#F0FDF4"
                      friends={locationFriends} selectedIds={realFriendIds} otherIds={displayedFriendIds} otherLabel={t(lang, "in_displayed_position")}
                      onToggle={(id) => toggleDoubleJeuFriend(id, "real")}
                      search={searchReal} onSearchChange={setSearchReal} lang={lang}
                    />

                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 18, padding: "12px 14px", borderRadius: 12, backgroundColor: "#F8F8F8" }}>
                      <Globe size={17} strokeWidth={1.8} color="#8A8A8A" />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{t(lang, "other_users_label")}</div>
                        <div style={{ fontSize: 12, color: "#999" }}>{t(lang, "other_users_sub")}</div>
                      </div>
                    </div>
                  </div>
                )}

                <button onClick={saveLocationSettings} disabled={savingLocationSettings} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer", marginTop: 18, opacity: savingLocationSettings ? 0.6 : 1 }}>
                  {savingLocationSettings ? "..." : t(lang, "save")}
                </button>
                {locationMode !== "discret" && (
                  <button onClick={() => { setShowLocationModal(false); navigate("/friends-map"); }} style={{ width: "100%", padding: "12px", borderRadius: 12, border: "1px solid #E0E0E0", backgroundColor: "white", color: "#0a0a0a", fontSize: 13, fontWeight: 600, cursor: "pointer", marginTop: 10 }}>
                    {t(lang, "view_friends_map")}
                  </button>
                )}
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
